import { useState } from 'react';
import { themeI18n } from '../utils/i18n_helper';
import { formatHour } from '../utils/time.js';
import { StoresDrawer } from './StoresDrawer.js';

import Icons from './Icons.js';

const myStore = JSON.parse(localStorage.getItem('theme:my_store'));
const storeLocationsData = JSON.parse(document.querySelector('[data-store-locations-data]').innerHTML).locations;

export const LocationsDrawer = ({ inventoryData, drawerName }) => {
  const [storeLocations, setStoreLocations] = useState(storeLocationsData); // consider using GraphQL to fetch store locations if many locations
  const [myStoreLocation, setMyStoreLocation] = useState(myStore);
  const [myStoreLocationSinceDrawerOpened, setMyStoreLocationSinceDrawerOpened] = useState();

  const myStoreData = myStoreLocation ? storeLocations.find(store => store.id === myStoreLocation) : null;

  return (
    <>
      <button
        className="locations-drawer__header-trigger hide-desktop-down"
        onClick={() => {
          window.dispatchEvent(new CustomEvent('open_locations_drawer' + drawerName))
          setMyStoreLocationSinceDrawerOpened(myStoreLocation);
        }}
      >
        <Icons icon="map-pin" className='locations-drawer__header-trigger-pin' />

        {myStoreLocation ? (
          <span className='locations-drawer__header-trigger-text' title={`${themeI18n('store_locator.my_store')} : ${myStoreData.name}`}>
            {themeI18n('store_locator.my_store')} : <strong>{myStoreData.name}</strong>
          </span>
        ):(
          <span className='locations-drawer__header-trigger-text'>
            {themeI18n('store_locator.select_a_store')}
          </span>
        )}

        <Icons icon="caret" className='locations-drawer__header-trigger-caret' />
      </button>

      <button
        className="locations-drawer__header-trigger-mobile hide-desktop-up"
        onClick={() => {
          window.dispatchEvent(new CustomEvent('open_locations_drawer' + drawerName))
          setMyStoreLocationSinceDrawerOpened(myStoreLocation);
        }}
      >
        <div className="locations-drawer__header-trigger-mobile-left">
          <Icons icon="map-pin" className='locations-drawer__header-trigger-pin' />

          {myStoreLocation ? (
            <span className='locations-drawer__header-trigger-text' title={`${themeI18n('store_locator.my_store')} : ${myStoreData.name}`}>
              {myStoreData.name}
            </span>
          ):(
            <span className='locations-drawer__header-trigger-text'>
              {themeI18n('store_locator.select_a_store')}
            </span>
          )}
        </div>

        <div className="locations-drawer__header-trigger-mobile-right">
          {myStoreData && <OpenIndicator store={myStoreData} />}
          <Icons icon="caret" className='locations-drawer__header-trigger-caret' />
        </div>
      </button>

      <StoresDrawer drawerName={drawerName} myStoreLocationSinceDrawerOpened={myStoreLocationSinceDrawerOpened} myStoreLocation={myStoreLocation} setMyStoreLocation={setMyStoreLocation}/>
    </>
  )
}

const OpenIndicator = ({ store }) => {
  const currentDay = new Date().getDay();
  const currentDate = new Date();
  const storeHours = {};
  const dayHoursKeys = ['sunday_hours', 'monday_hours', 'tuesday_hours', 'wednesday_hours', 'thursday_hours', 'friday_hours', 'saturday_hours'];

  dayHoursKeys.forEach((day, index) => {
    storeHours[index] = {
      open: store[day]?.split("-")[0].split(":")[0],
      openMinutes: store[day]?.split("-")[0].split(":")[1],
      close: store[day]?.split("-")[1].split(":")[0],
      closeMinutes: store[day]?.split("-")[1].split(":")[1]
    }
  });

  const storeOpeningDate = new Date();
  storeOpeningDate.setHours(storeHours[currentDay].open);
  storeOpeningDate.setMinutes(storeHours[currentDay].openMinutes);
  const storeClosingDate = new Date();
  storeClosingDate.setHours(storeHours[currentDay].close);
  storeClosingDate.setMinutes(storeHours[currentDay].closeMinutes);

  const isOpen = currentDate >= storeOpeningDate && currentDate <= storeClosingDate && storeHours[currentDay].close && storeHours[currentDay].open;

  if (isOpen) {
    const title = themeI18n('store_locator.open') + themeI18n('store_locator.until') + formatHour(storeHours[currentDay].close, storeHours[currentDay].closeMinutes);

    return (
      <span className='locations-drawer__open-indicator'>
        <span className='locations-drawer__open-indicator-text-blurb single-line-text' title={title}>
          <span className='locations-drawer__header-trigger-store-status-highlight--open'>
            {themeI18n('store_locator.open')}
          </span>
          {themeI18n('store_locator.until')}
          {formatHour(storeHours[currentDay].close, storeHours[currentDay].closeMinutes)}
        </span>
      </span>
    )
  } else {
    const isOpeningLater = currentDate < storeOpeningDate;

    const title = themeI18n('store_locator.closed') + themeI18n('store_locator.opens_at') + formatHour(storeHours[currentDay].open, storeHours[currentDay].openMinutes);

    if (isOpeningLater) {
      return (
        <span className='locations-drawer__open-indicator'>
          <span className='locations-drawer__open-indicator-text-blurb single-line-text' title={title}>
            <span className='locations-drawer__header-trigger-store-status-highlight--closed'>
              {themeI18n('store_locator.closed')}
            </span>
            {themeI18n('store_locator.opens_at')}
            {formatHour(storeHours[currentDay].open, storeHours[currentDay].openMinutes)}
          </span>
        </span>
      )
    } else {
      let nextOpenDay;

      for (let i = currentDay + 1; i <= 6; i++) {
        if (storeHours[i].open) {
          nextOpenDay = i;
          break;
        }
      }

      if (!nextOpenDay) {
        for (let i = 0; i <= currentDay; i++) {
          if (storeHours[i].open) {
            nextOpenDay = i;
            break;
          }
        }
      }

      const title = themeI18n('store_locator.closed') + themeI18n('store_locator.opens_on') + themeI18n(`store_locator.days.${nextOpenDay}`) + themeI18n('store_locator.at') + formatHour(storeHours[nextOpenDay].open, storeHours[nextOpenDay].openMinutes);

      return (
        <span className='locations-drawer__open-indicator'>
          <span className='locations-drawer__open-indicator-text-blurb single-line-text' title={title}>
            <span className='locations-drawer__header-trigger-store-status-highlight--closed'>
              {themeI18n('store_locator.closed')}
            </span>
            {themeI18n('store_locator.opens_on')}
            {themeI18n(`store_locator.days.${nextOpenDay}`)}
            {themeI18n('store_locator.at')}
            {formatHour(storeHours[nextOpenDay].open, storeHours[nextOpenDay].openMinutes)}
          </span>
        </span>
      )
    }
  }
}
